import React, { useState, useEffect, useRef } from "react";
import QuestionItem from "../components/QuestionItem";
import QuestionsAnswersArray from "../components/QuestionsAnswersArray";
import { CButton, CCollapse } from "@coreui/react";
import useQuestions from "../hooks/useQuestions";
import { toast } from "react-toastify";
import { ReactComponent as PlusIcon } from "../assets/images/icons/plus.svg";
import { ReactComponent as BinIcon } from "../assets/images/icons/bin.svg";
import { ReactComponent as CopyIcon } from "../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../assets/images/icons/copy.svg";
import { Tooltip } from "react-tooltip";
import { FormArray, FormBuilder } from "react-reactive-form";
import useModalWindow from "../hooks/useModalWindow";
import {
  calculateInitialCompBlockIds,
  addCompPkBlock,
  removeCompPkBlock,
  duplicateCompPkBlock,
  updatePkAnalytesComparisonAdditionalData,
} from "./utils/comparativePKUtils";

const ComparativePKQuestions = ({ globalId, wizardForm }) => {
  const [compBlockIds, setCompBlockIds] = useState(() =>
    calculateInitialCompBlockIds(wizardForm)
  );
  const nextBlockIdRef = useRef(Math.max(...compBlockIds, 0) + 1);
  const lastBlockRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const { questionsDataSaved } = useQuestions();
  const { modalWindow } = useModalWindow();
  const [openBlockIds, setOpenBlockIds] = useState(
    new Set([compBlockIds[compBlockIds.length - 1]])
  );

  // Calculate Standard PK blocks count for conflict detection
  const calculateStdBlocksCount = () => {
    const formValues = wizardForm.value;
    if (formValues.qPkStdAnalysis && Array.isArray(formValues.qPkStdAnalysis)) {
      return Math.max(0, formValues.qPkStdAnalysis.length);
    }
    const stdColumnControls = Object.keys(wizardForm.controls).filter(
      key => key.startsWith("qPkStdColumn") && !key.includes("Comp")
    );
    if (stdColumnControls.length > 0) {
      const maxBlock = Math.max(
        ...stdColumnControls.map(key => {
          const match = key.match(/qPkStdColumn(_\d+)?$/);
          if (match && match[1]) {
            return parseInt(match[1].substring(1));
          }
          return 1;
        })
      );
      return Math.max(1, maxBlock);
    }
    return 0;
  };

  const [pkStdBlocksCount, setPkStdBlocksCount] = useState(
    calculateStdBlocksCount()
  );

  // const dash = " - ";
  const dash = "";

  // Track if we're currently initializing to prevent infinite loops
  const isInitializingRef = useRef(false);
  const compControlsInitializedRef = useRef(new Set());
  const prevCompEnabledRef = useRef(wizardForm.controls.qPkCompEnabled?.value);
  const prevCompBlockIdsRef = useRef(compBlockIds.join(","));
  const prevStdBlocksCountRef = useRef(pkStdBlocksCount);

  // Update Standard PK blocks count when form changes
  useEffect(() => {
    const newStdCount = calculateStdBlocksCount();
    if (newStdCount !== pkStdBlocksCount) {
      setPkStdBlocksCount(newStdCount);
    }
  }, [wizardForm.value.qPkStdAnalysis, wizardForm.controls]);

  // Update Comparative PK block IDs when form values are restored
  useEffect(() => {
    const newIds = calculateInitialCompBlockIds(wizardForm);
    const currentKey = compBlockIds.join(",");
    const newKey = newIds.join(",");
    if (newKey !== currentKey) {
      setCompBlockIds(newIds);
      nextBlockIdRef.current = Math.max(...newIds, 0) + 1;
    }
  }, [wizardForm.value.qPkCompAnalysis, wizardForm.controls]);

  // Initialize Comparative block controls when Comparative PK is enabled
  useEffect(() => {
    const compEnabled =
      wizardForm.controls.qPkCompEnabled?.value === "true" ||
      questionsDataSaved?.wizardForm?.qPkCompEnabled === "true";
    const compBlockIdsKey = compBlockIds.join(",");
    const compEnabledChanged = prevCompEnabledRef.current !== compEnabled;
    const compBlockIdsChanged = prevCompBlockIdsRef.current !== compBlockIdsKey;
    const stdBlocksCountChanged =
      prevStdBlocksCountRef.current !== pkStdBlocksCount;

    // Only run if something actually changed
    if (!compEnabledChanged && !compBlockIdsChanged && !stdBlocksCountChanged) {
      return;
    }

    // Update refs
    prevCompEnabledRef.current = compEnabled;
    prevCompBlockIdsRef.current = compBlockIdsKey;
    prevStdBlocksCountRef.current = pkStdBlocksCount;

    if (compEnabled) {
      isInitializingRef.current = true;

      try {
        // Always use "Comp" prefix for shared fields (isolation from Standard PK)
        const sharedFieldPrefix = "Comp";

        // Initialize controls for each Comparative block
        compBlockIds.forEach(blockId => {
          const suffix = blockId > 1 ? `_${blockId}` : "";
          const blockKey = `comp-${blockId}-${sharedFieldPrefix}`;

          // Skip if we've already initialized this block (unless IDs or counts changed)
          if (
            compControlsInitializedRef.current.has(blockKey) &&
            !compBlockIdsChanged &&
            !stdBlocksCountChanged
          ) {
            return;
          }

          // Initialize shared fields with prefixed controls if needed
          const sharedFields = [
            { name: `qPkTable${sharedFieldPrefix}${suffix}`, defaultValue: "" },
            {
              name: `qPkPopulation${sharedFieldPrefix}${suffix}`,
              defaultValue: "",
            },
            {
              name: `qPkMultiDose${sharedFieldPrefix}${suffix}`,
              defaultValue: "false",
            },
            {
              name: `qPkFigures${sharedFieldPrefix}${suffix}`,
              defaultValue: [],
            },
            {
              name: `qPkFiguresExtra${sharedFieldPrefix}${suffix}`,
              defaultValue: [],
            },
            {
              name: `qPkTimelines${sharedFieldPrefix}${suffix}`,
              defaultValue: "",
            },
            {
              name: `qPkStatsAnalysis${sharedFieldPrefix}${suffix}`,
              defaultValue: [],
            },
          ];

          sharedFields.forEach(({ name, defaultValue }) => {
            if (!wizardForm.contains(name)) {
              if (name.startsWith("qPkTimelines")) {
                wizardForm.addControl(name, new FormArray([]));
              } else {
                // Check if there's a restored value in wizardForm.value before using default
                const restoredValue = wizardForm.value[name];
                const initialValue =
                  restoredValue !== undefined &&
                  restoredValue !== null &&
                  restoredValue !== ""
                    ? restoredValue
                    : defaultValue;
                wizardForm.addControl(name, FormBuilder.control(initialValue));
              }
            } else if (name.startsWith("qPkTimelines")) {
              // Ensure existing qPkTimelines control is a FormArray
              const existingControl = wizardForm.get(name);
              if (!(existingControl instanceof FormArray)) {
                // Preserve existing value if it was restored
                const existingValue = existingControl?.value;
                wizardForm.removeControl(name);
                const newArray = new FormArray([]);
                // If there was a restored value, try to preserve it
                if (existingValue) {
                  if (
                    Array.isArray(existingValue) &&
                    existingValue.length > 0
                  ) {
                    existingValue.forEach(item => {
                      if (
                        item &&
                        typeof item === "object" &&
                        item.valueHelper
                      ) {
                        newArray.push(
                          FormBuilder.group({ valueHelper: [item.valueHelper] })
                        );
                      } else if (
                        typeof item === "string" &&
                        item.trim() !== ""
                      ) {
                        newArray.push(
                          FormBuilder.group({ valueHelper: [item] })
                        );
                      }
                    });
                  } else if (
                    typeof existingValue === "string" &&
                    existingValue.trim() !== ""
                  ) {
                    newArray.push(
                      FormBuilder.group({ valueHelper: [existingValue] })
                    );
                  }
                }
                wizardForm.addControl(name, newArray);
              }
            }
            // Note: For existing controls, we preserve their values - they should already be set by restore
          });

          // Initialize Comparative-specific fields
          const compSpecificFields = [
            { name: `qPkComparisonName${suffix}`, defaultValue: "" },
            { name: `qPkComparisonPairs${suffix}`, defaultValue: [] },
            { name: `qPkAnalytes${suffix}`, defaultValue: [] },
            { name: `qPkExposure${suffix}`, defaultValue: [] },
            { name: `qPkDoseInteraction${suffix}`, defaultValue: "false" },
          ];

          compSpecificFields.forEach(({ name, defaultValue }) => {
            if (!wizardForm.contains(name)) {
              if (
                name === `qPkAnalytes${suffix}` ||
                name === `qPkComparisonPairs${suffix}` ||
                name === `qPkExposure${suffix}`
              ) {
                wizardForm.addControl(name, new FormArray([]));
              } else {
                wizardForm.addControl(name, FormBuilder.control(defaultValue));
              }
            }
          });

          // Mark this block as initialized
          compControlsInitializedRef.current.add(blockKey);
        });

        // Clean up initialization keys for blocks that no longer exist
        const currentBlockKeys = new Set(
          compBlockIds.map(blockId => `comp-${blockId}-${sharedFieldPrefix}`)
        );
        compControlsInitializedRef.current.forEach(key => {
          if (!currentBlockKeys.has(key)) {
            compControlsInitializedRef.current.delete(key);
          }
        });
      } finally {
        setTimeout(() => {
          isInitializingRef.current = false;
        }, 0);
      }
    } else {
      compControlsInitializedRef.current.clear();
    }
  }, [
    wizardForm.controls.qPkCompEnabled?.value,
    compBlockIds,
    pkStdBlocksCount,
    wizardForm.controls.qPkStdEnabled?.value,
    questionsDataSaved?.wizardForm?.qPkCompEnabled,
  ]);

  useEffect(() => {
    if (shouldScrollRef.current && lastBlockRef.current) {
      const el = lastBlockRef.current;
      let wrapper = el.parentElement;
      while (
        wrapper &&
        !wrapper.classList.contains("dashboard-content__wrapper")
      ) {
        wrapper = wrapper.parentElement;
      }
      if (wrapper) {
        const elTop = el.getBoundingClientRect().top;
        const wrapperTop = wrapper.getBoundingClientRect().top;
        wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
      }
      shouldScrollRef.current = false;
    }
  }, [compBlockIds.length]);

  const handleAddBlock = e => {
    console.log(e);
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    addCompPkBlock(wizardForm, newId);
    shouldScrollRef.current = true;
    setCompBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
  };

  const handleDuplicateBlock = (e, displayIndex) => {
    if (e) e.preventDefault();
    const sourceBlockId = compBlockIds[displayIndex];
    if (sourceBlockId === undefined) return;
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    duplicateCompPkBlock(wizardForm, sourceBlockId, newId);
    shouldScrollRef.current = true;
    setCompBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
    toast.success("Block duplicated successfully");
  };

  const handleDuplicateLastBlock = e => {
    if (e) e.preventDefault();
    if (compBlockIds.length === 0) return;
    handleDuplicateBlock(e, compBlockIds.length - 1);
  };

  const handleRemoveBlock = async (e, displayIndex) => {
    if (e) e.preventDefault();
    if (compBlockIds.length <= 1) return;
    const confirmed = await modalWindow({
      title: "Remove Block",
      message: "Do you want to remove this block of questions?",
      confirmButtonLabel: "Yes",
    });
    if (!confirmed) return;
    const blockId = compBlockIds[displayIndex];
    removeCompPkBlock(wizardForm, blockId);
    const remainingIds = compBlockIds.filter((_, i) => i !== displayIndex);
    setCompBlockIds(remainingIds);
    setOpenBlockIds(new Set([remainingIds[remainingIds.length - 1]]));
  };

  const renderBlockContent = (blockId, displayIndex) => {
    const suffix = blockId > 1 ? `_${blockId}` : "";
    const isFirstBlock = displayIndex === 0;
    const blockNumber = displayIndex + 1;

    // Always use "Comp" prefix for shared fields to guarantee isolation from Standard PK
    const sharedFieldPrefix = "Comp";

    // Ensure controls exist before rendering
    const qPkFiguresControlName = `qPkFigures${sharedFieldPrefix}${suffix}`;
    const qPkFiguresExtraControlName = `qPkFiguresExtra${sharedFieldPrefix}${suffix}`;
    const qPkTimelinesControlName = `qPkTimelines${sharedFieldPrefix}${suffix}`;
    const qPkStatsAnalysisControlName = `qPkStatsAnalysis${sharedFieldPrefix}${suffix}`;

    if (!wizardForm.contains(qPkFiguresControlName)) {
      wizardForm.addControl(qPkFiguresControlName, FormBuilder.control([]));
    }
    if (!wizardForm.contains(qPkFiguresExtraControlName)) {
      wizardForm.addControl(
        qPkFiguresExtraControlName,
        FormBuilder.control([])
      );
    }
    if (!wizardForm.contains(qPkTimelinesControlName)) {
      wizardForm.addControl(qPkTimelinesControlName, new FormArray([]));
    } else {
      const existingControl = wizardForm.get(qPkTimelinesControlName);
      if (!(existingControl instanceof FormArray)) {
        wizardForm.removeControl(qPkTimelinesControlName);
        wizardForm.addControl(qPkTimelinesControlName, new FormArray([]));
      }
    }
    if (!wizardForm.contains(qPkStatsAnalysisControlName)) {
      wizardForm.addControl(
        qPkStatsAnalysisControlName,
        FormBuilder.control([])
      );
    }

    // Whole-number indexing: 1, 2, 3, 4, ... ; extra blocks use "2 - (blockNumber)." etc.
    const q = n => (isFirstBlock ? `${n}` : `${n}${dash}(${blockNumber})`);
    const questionIndex1a = q(2);
    const questionIndex1b = q(3);
    const questionIndex1c = q(4);
    const questionIndex1d = q(5);
    const questionIndex1e = q(6);
    const questionIndex1f = q(7);
    const questionIndex1g = q(8);
    const questionIndex1h = q(9);
    const questionIndex1i = q(10);
    const questionIndex1j = q(11);
    const questionIndex1k = q(12);
    const questionIndex1l = q(13);

    return (
      <>
        {wizardForm.controls.qPkCompEnabled?.value === "true" && (
          <div
            style={{
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
            }}>
            <CButton
              color={"primary"}
              shape={"pill"}
              data-tooltip-id={`duplicate-comp-pk-block-${blockId}`}
              style={{
                border: "1px solid #006dee",
                background: "#006dee",
                fontSize: "15px",
                color: "#fff",
                boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
              }}
              onClick={e => handleDuplicateBlock(e, displayIndex)}>
              <CopyIcon /> Duplicate
            </CButton>
            <Tooltip
              id={`duplicate-comp-pk-block-${blockId}`}
              place="top"
              delayShow={100}
              className="custom-tooltip">
              Duplicate this block
            </Tooltip>
            {!isFirstBlock && (
              <>
                <CButton
                  color={"danger"}
                  shape={"pill"}
                  data-tooltip-id={`remove-comp-pk-block-${blockId}`}
                  onClick={e => handleRemoveBlock(e, displayIndex)}>
                  <BinIcon />
                </CButton>
                <Tooltip
                  id={`remove-comp-pk-block-${blockId}`}
                  place="top"
                  delayShow={100}
                  className="custom-tooltip">
                  Remove this block
                </Tooltip>
              </>
            )}
          </div>
        )}

        <QuestionItem
          questionIndex={questionIndex1a}
          questionName={`qPkTable${sharedFieldPrefix}${suffix}`}
          questionsName={"qPkTable"}
          suffix={suffix}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainSingleSelectDropdown={true}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1b}
          questionName={`qPkPopulation${sharedFieldPrefix}${suffix}`}
          questionsName={"qPkPopulation"}
          suffix={suffix}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          questionInputType={"text"}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1c}
          questionName={`qPkMultiDose${sharedFieldPrefix}${suffix}`}
          questionsName={"qPkMultiDose"}
          suffix={suffix}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          questionInputType={"radio"}
          questionInputChoice={"qPkMultiDose"}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1d}
          questionName={`qPkComparisonName${suffix}`}
          questionsName={"qPkComparisonName"}
          suffix={suffix}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          questionInputType={"text"}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1e}
          questionName={qPkFiguresControlName}
          questionsName={"qPkFigures"}
          suffix={suffix}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainMultiSelectDropdown={true}
          tables={false}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1f}
          questionName={qPkFiguresExtraControlName}
          questionsName={"qPkFiguresExtra"}
          suffix={suffix}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainMultiSelectDropdown={true}
          tables={false}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1g}
          controlName={`qPkAnalytes${suffix}`}
          questionsName={"qPkAnalytes"}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "richtext"]]}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1h}
          controlName={`qPkComparisonPairs${suffix}`}
          questionsName={"qPkComparisonPairs"}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          formGroup={wizardForm}
          inputTextBoxes={[
            ["Analyte", "Analyte", "select"],
            ["Comparator", "Comparator", "select"],
          ]}
          inputTextBoxesAdditionalData={() =>
            updatePkAnalytesComparisonAdditionalData(wizardForm, suffix)
          }
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1i}
          controlName={`qPkExposure${suffix}`}
          questionsName={"qPkExposure"}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "text"]]}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1j}
          questionName={`qPkDoseInteraction${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          questionInputType={"radio"}
          questionInputChoice={"qPkDoseInteraction"}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1k}
          controlName={qPkTimelinesControlName}
          questionsName={"qPkTimelines"}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "text"]]}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1l}
          questionName={qPkStatsAnalysisControlName}
          questionsName={"qPkStatsAnalysis"}
          suffix={suffix}
          activator={wizardForm.controls.qPkCompEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainMultiSelectDropdown={true}
          tables={true}
          globalId={globalId}
        />
      </>
    );
  };

  return (
    <>
      <QuestionItem
        questionIndex={"1"}
        questionName={"qPkCompEnabled"}
        activator={true}
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />

      {/* Comparative PK Analysis Blocks */}
      {wizardForm.controls.qPkCompEnabled?.value === "true" && (
        <>
          {compBlockIds.length === 1 ? (
            <div
              ref={lastBlockRef}
              className="analysis-block"
              //style={{ marginBottom: "2rem" }}
            >
              {renderBlockContent(compBlockIds[0], 0)}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                }}>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set(compBlockIds))}>
                  Expand all
                </CButton>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set())}>
                  Collapse all
                </CButton>
              </div>
              <div className="accordion">
                {compBlockIds.map((blockId, displayIndex) => {
                  const isOpen = openBlockIds.has(blockId);
                  return (
                    <div
                      key={`pk-comp-block-${blockId}`}
                      className="accordion-item"
                      ref={
                        displayIndex === compBlockIds.length - 1
                          ? lastBlockRef
                          : null
                      }>
                      <div className="accordion-header">
                        <button
                          type="button"
                          className={`accordion-button${isOpen ? "" : " collapsed"}`}
                          onClick={() =>
                            setOpenBlockIds(prev => {
                              const next = new Set(prev);
                              next.has(blockId)
                                ? next.delete(blockId)
                                : next.add(blockId);
                              return next;
                            })
                          }>
                          Block {displayIndex + 1}
                        </button>
                      </div>
                      <CCollapse
                        visible={isOpen}
                        className="accordion-collapse">
                        <div className="accordion-body">
                          <div
                            className="analysis-block"
                            //style={{ marginBottom: "2rem" }}
                          >
                            {renderBlockContent(blockId, displayIndex)}
                          </div>
                        </div>
                      </CCollapse>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {wizardForm.controls.qPkCompEnabled?.value === "true" && (
            <div
              style={{
                marginTop: "1rem",
                marginBottom: "2rem",
                display: "flex",
                gap: "0.5rem",
              }}>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleAddBlock}>
                Add another comparative PK Analysis <PlusIcon />
              </CButton>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleDuplicateLastBlock}>
                Duplicate block above <CopyOutlineIcon />
              </CButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ComparativePKQuestions;
